import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Download,
  Filter,
  Store,
  RefreshCcw,
  Loader2,
  Tag,
  ArrowUpDown,
  Activity,
  Calendar,
  Printer as PrintIcon,
  CheckCircle,
  FileText,
  Layers,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";

export const InventoryMovementReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    category_id: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
    start_date: "",
    end_date: "",
    movement_type: "all",
    limit: 50,
    sort_by: "quantity_sold",
    sort_order: "desc",
  }));
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);

  const summaryStats = previewData?.summary || null;

  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCategorySearch(categorySearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [categorySearch]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // ==================== QUERIES ====================
  const { data: categories = [], isFetching: isCategoriesFetching } = useQuery({
    enabled: hasPermission("report inventory movement view"),
    queryKey: ["categories", debouncedCategorySearch],
    queryFn: async () => {
      const params = {};
      if (debouncedCategorySearch) {
        params.search = debouncedCategorySearch;
      }
      const response = await apiClient.get(`/category_product`, {
        params,
      });
      return response.data.data;
    },
  });

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report inventory movement view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report inventory movement view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (
        currentFilters.movement_type &&
        currentFilters.movement_type !== "all"
      )
        params.movement_type = currentFilters.movement_type;
      if (currentFilters.limit) params.limit = Number(currentFilters.limit);
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.INVENTORY_MOVEMENT,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report inventory movement view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (
        currentFilters.movement_type &&
        currentFilters.movement_type !== "all"
      )
        params.movement_type = currentFilters.movement_type;
      if (currentFilters.limit) params.limit = Number(currentFilters.limit);
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.INVENTORY_MOVEMENT,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Inventory movement report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate inventory movement report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating inventory movement report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      category_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      start_date: "",
      end_date: "",
      movement_type: "all",
      limit: 50,
      sort_by: "quantity_sold",
      sort_order: "desc",
    });
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `inventory-movement-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const categoryOptions = categories.map((category) => ({
    value: String(category.id),
    label: category.name,
  }));

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <Activity className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Inventory Analytics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Inventory Movement
              </span>
            </div>
          }
          subtitle="Analyze fast and slow moving items to optimize your stock levels."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            className="rounded-xl mr-2"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report inventory movement view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report inventory movement view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-8">
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Filtering Criteria</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Define parameters for your movement analysis
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="p-8">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                        <Store className="h-4 w-4 text-primary" /> Location &
                        Type
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">
                            Warehouse / Store
                          </Label>
                          <ReactSelect
                            options={storeOptions}
                            value={
                              storeOptions.find(
                                (opt) => opt.value === String(filters.store_id || ""),
                              ) ||
                              (filters.store_id &&
                                currentStore?.id &&
                                String(filters.store_id) === String(currentStore.id)
                                ? {
                                  value: String(currentStore.id),
                                  label: currentStore.name,
                                }
                                : null)
                            }
                            onChange={(opt) =>
                              setFilters((p) => ({
                                ...p,
                                store_id: opt?.value || "",
                              }))
                            }
                            placeholder="All Locations"
                            isClearable={Boolean(filters.store_id)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">
                            Movement Type
                          </Label>
                          <Select
                            value={filters.movement_type}
                            onValueChange={(val) =>
                              setFilters((p) => ({ ...p, movement_type: val }))
                            }
                          >
                            <SelectTrigger className="bg-muted/30 border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Items</SelectItem>
                              <SelectItem value="fast">Fast Moving</SelectItem>
                              <SelectItem value="slow">Slow Moving</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                        <Tag className="h-4 w-4 text-primary" /> Categorization
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">
                            Product Category
                          </Label>
                          <ReactSelect
                            options={categoryOptions}
                            isLoading={isCategoriesFetching}
                            value={categoryOptions.find(
                              (opt) =>
                                opt.value === String(filters.category_id),
                            )}
                            onInputChange={(val, { action }) => {
                              if (action === "input-change") {
                                setCategorySearch(val);
                              }
                            }}
                            onChange={(opt) =>
                              setFilters((p) => ({
                                ...p,
                                category_id: opt?.value || "",
                              }))
                            }
                            placeholder="All Categories"
                            isClearable
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">
                            Limit Results (1-100)
                          </Label>
                          <Input
                            type="number"
                            name="limit"
                            value={filters.limit}
                            onChange={handleFilterChange}
                            min="1"
                            max="100"
                            className="bg-muted/30 border-border focus:bg-background transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2 mb-6">
                      <Calendar className="h-4 w-4 text-primary" /> Timeframe &
                      Sort
                    </h3>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">
                              Start Date
                            </Label>
                            <Input
                              type="date"
                              name="start_date"
                              value={filters.start_date}
                              onChange={handleFilterChange}
                              className="bg-muted/30 border-border focus:bg-background transition-colors"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">
                              End Date
                            </Label>
                            <Input
                              type="date"
                              name="end_date"
                              value={filters.end_date}
                              onChange={handleFilterChange}
                              className="bg-muted/30 border-border focus:bg-background transition-colors"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">
                              Sort By
                            </Label>
                            <Select
                              value={filters.sort_by}
                              onValueChange={(val) =>
                                setFilters((p) => ({ ...p, sort_by: val }))
                              }
                            >
                              <SelectTrigger className="bg-muted/30 border-border">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="product_name">
                                  Product Name
                                </SelectItem>
                                <SelectItem value="quantity_sold">
                                  Quantity Sold
                                </SelectItem>
                                <SelectItem value="total_revenue">
                                  Total Revenue
                                </SelectItem>
                                <SelectItem value="turnover_rate">
                                  Turnover Rate
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">
                              Order
                            </Label>
                            <Select
                              value={filters.sort_order}
                              onValueChange={(val) =>
                                setFilters((p) => ({ ...p, sort_order: val }))
                              }
                            >
                              <SelectTrigger className="bg-muted/30 border-border">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="desc">Descending</SelectItem>
                                <SelectItem value="asc">Ascending</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <Card className="shadow-none border border-border/50 bg-primary/5 h-full">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="h-5 w-5 text-primary" />
                        Report Guidance
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6 text-sm text-muted-foreground">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-foreground">
                          Movement Type
                        </h4>
                        <p>
                          Filter products by their sales velocity. Fast moving
                          indicates high demand, while slow moving highlights
                          stagnant inventory.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-foreground">
                          Turnover Rate
                        </h4>
                        <p>
                          Sort by Turnover Rate to see which items are being
                          replaced most frequently relative to their stock
                          levels.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-foreground">
                          Limit Results
                        </h4>
                        <p>
                          Restrict the number of items returned (e.g. top 10
                          fast-moving items) for a more focused analysis.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {summaryStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Products
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {summaryStats.total_products}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Quantity Sold
                    </p>
                    <h3 className="text-2xl font-bold text-primary">
                      {summaryStats.total_quantity_sold}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50 col-span-1 md:col-span-2 lg:col-span-1 xl:col-span-2">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Revenue
                    </p>
                    <h3 className="text-2xl font-bold text-green-600">
                      {currencySymbol}
                      {(
                        parseFloat(summaryStats.total_revenue) || 0
                      ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Fast Moving
                    </p>
                    <h3 className="text-2xl font-bold text-emerald-500">
                      {summaryStats.fast_moving}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Moderate Moving
                    </p>
                    <h3 className="text-2xl font-bold text-blue-500">
                      {summaryStats.moderate_moving}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Slow/Dead Stock
                    </p>
                    <h3 className="text-2xl font-bold text-rose-500">
                      {(summaryStats.slow_moving || 0) +
                        (summaryStats.dead_stock || 0)}
                    </h3>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="shadow-lg border-none ring-1 ring-border/50 overflow-hidden">
              <CardHeader className="bg-muted/30 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  Product Movement Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="font-semibold text-foreground">
                          Product / SKU
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Category
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Qty Sold
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Current Stock
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Turnover Rate
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Revenue
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData?.data?.length > 0 ? (
                        previewData.data.map((item, idx) => (
                          <TableRow
                            key={item.product_id || idx}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <TableCell className="font-medium">
                              <div className="flex flex-col">
                                <span className="text-foreground">
                                  {item.product_name || "N/A"}
                                </span>
                                {item.sku && (
                                  <span className="text-xs text-muted-foreground">
                                    SKU: {item.sku}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {item.movement_category === "fast" && (
                                <Badge className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 border-emerald-500/20">
                                  Fast Moving
                                </Badge>
                              )}
                              {item.movement_category === "moderate" && (
                                <Badge className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border-blue-500/20">
                                  Moderate
                                </Badge>
                              )}
                              {(item.movement_category === "slow" ||
                                item.movement_category === "dead") && (
                                  <Badge className="bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 border-rose-500/20">
                                    {item.movement_category === "dead"
                                      ? "Dead Stock"
                                      : "Slow Moving"}
                                  </Badge>
                                )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {item.quantity_sold || 0}
                            </TableCell>
                            <TableCell className="text-right">
                              {item.current_stock !== undefined
                                ? item.current_stock
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {item.turnover_rate !== undefined
                                ? item.turnover_rate > 100
                                  ? "> 100"
                                  : Number(item.turnover_rate).toFixed(2)
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right font-bold text-foreground">
                              {currencySymbol}
                              {(
                                parseFloat(item.total_revenue) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="h-32 text-center text-muted-foreground"
                          >
                            No data found for the selected filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Compiled!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Your Inventory Movement report has been successfully generated
                  and is ready for download or printing.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

